import type { QueryClient, QueryFunctionContext } from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import { preservePendingManualRows } from "./spreadsheet-cache/pending-manual-rows";
import type {
  IAiSpreadsheetSheetResponse,
  CachedSpreadsheetSheetResponse,
} from "./types";
import type { WorkbookReadSource } from "./workbook-read-source";

export const getAiScriptSpreadsheetSheetPath = (
  businessId: string,
  runId: string,
  sheetId: string,
  source?: WorkbookReadSource,
) =>
  source
    ? `${source.workbookPath}/sheets/${sheetId}`
    : `/businesses/${businessId}/ai_script_runs/${runId}/spreadsheet/sheets/${sheetId}`;

export const aiScriptSpreadsheetSheetKey = (
  businessId: string | null,
  runId: string | null,
  sheetId: string | null,
  source?: WorkbookReadSource,
) =>
  source
    ? ([source.cacheKey, businessId, runId, "sheet", sheetId] as const)
    : (["ai-script-spreadsheet-sheet", businessId, runId, sheetId] as const);

export const aiScriptSpreadsheetSheetListKey = (
  businessId: string | null,
  runId: string | null,
) => ["ai-script-spreadsheet-sheet", businessId, runId] as const;

export const aiScriptSpreadsheetSheetQueryOptions = (
  api: AxiosInstance,
  businessId: string | null,
  runId: string | null,
  sheetId: string | null,
  requestSignal?: AbortSignal,
  queryClient?: QueryClient,
) => ({
  queryKey: aiScriptSpreadsheetSheetKey(businessId, runId, sheetId),
  queryFn: ({ signal }: QueryFunctionContext) => {
    if (!businessId || !runId || !sheetId) {
      throw new Error("Payroll Grid sheet is required");
    }

    return api
      .get(getAiScriptSpreadsheetSheetPath(businessId, runId, sheetId), {
        signal: requestSignal ?? signal,
      })
      .then((res) =>
        preservePendingManualRows(
          queryClient?.getQueryData<CachedSpreadsheetSheetResponse>(
            aiScriptSpreadsheetSheetKey(businessId, runId, sheetId),
          ),
          res.data as IAiSpreadsheetSheetResponse,
        ),
      );
  },
  retry: false,
  staleTime: Infinity,
});

interface RefreshChangedSpreadsheetSheetsArgs {
  queryClient: QueryClient;
  businessId: string;
  runId: string;
  changedSheetIds: string[];
  immediateSheetId?: string;
}

export const refreshChangedSpreadsheetSheets = async ({
  queryClient,
  businessId,
  runId,
  changedSheetIds,
  immediateSheetId,
}: RefreshChangedSpreadsheetSheetsArgs) => {
  await Promise.all(
    changedSheetIds.map((sheetId) =>
      queryClient.invalidateQueries({
        queryKey: aiScriptSpreadsheetSheetKey(businessId, runId, sheetId),
        exact: true,
        refetchType: "none",
      }),
    ),
  );

  if (!immediateSheetId || !changedSheetIds.includes(immediateSheetId)) return;
  await queryClient.refetchQueries({
    queryKey: aiScriptSpreadsheetSheetKey(businessId, runId, immediateSheetId),
    exact: true,
    type: "all",
  });
};

const WARM_CHANGED_SHEET_DELAY_MS = 150;
const warmupControllers = new Map<string, AbortController>();

const warmupKey = (businessId: string, runId: string) =>
  `${businessId}:${runId}`;

const wait = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve) => {
    if (signal?.aborted) {
      resolve();
      return;
    }

    const onAbort = () => {
      globalThis.clearTimeout(timeout);
      resolve();
    };
    const timeout = globalThis.setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal?.addEventListener("abort", onAbort, { once: true });
  });

interface WarmChangedSpreadsheetSheetsArgs {
  api: AxiosInstance;
  queryClient: QueryClient;
  businessId: string;
  runId: string;
  sheetIds: string[];
  signal?: AbortSignal;
}

/**
 * Warms stale, cached sheets sequentially after recalculation. This is only a
 * responsiveness optimization: callers retain broad invalidation as the
 * correctness boundary, so an omitted or failed sheet still refetches on use.
 */
export const warmChangedSpreadsheetSheets = async ({
  api,
  queryClient,
  businessId,
  runId,
  sheetIds,
  signal,
}: WarmChangedSpreadsheetSheetsArgs) => {
  let warmedAnySheet = false;

  for (const sheetId of Array.from(new Set(sheetIds))) {
    if (signal?.aborted) return;

    const state = queryClient.getQueryState(
      aiScriptSpreadsheetSheetKey(businessId, runId, sheetId),
    );

    if (!state || state.fetchStatus === "fetching") continue;

    if (warmedAnySheet) await wait(WARM_CHANGED_SHEET_DELAY_MS, signal);
    if (signal?.aborted) return;

    try {
      await queryClient.prefetchQuery(
        aiScriptSpreadsheetSheetQueryOptions(
          api,
          businessId,
          runId,
          sheetId,
          signal,
          queryClient,
        ),
      );
    } catch {
      // Lazy refetch remains the fallback when background warming fails.
    }
    warmedAnySheet = true;
  }
};

export const startChangedSpreadsheetSheetWarmup = (
  args: Omit<WarmChangedSpreadsheetSheetsArgs, "signal">,
) => {
  const key = warmupKey(args.businessId, args.runId);
  warmupControllers.get(key)?.abort();
  const controller = new AbortController();
  warmupControllers.set(key, controller);

  return warmChangedSpreadsheetSheets({
    ...args,
    signal: controller.signal,
  }).finally(() => {
    if (warmupControllers.get(key) === controller) {
      warmupControllers.delete(key);
    }
  });
};

export const cancelChangedSpreadsheetSheetWarmup = (
  businessId: string,
  runId: string,
) => {
  const key = warmupKey(businessId, runId);
  warmupControllers.get(key)?.abort();
  warmupControllers.delete(key);
};
